/*
  calculatorPdf.js — building the payload for, and handling the response from, the CGS
  calculator PDF endpoint.

  ENDPOINT
  POST /api/Calculator/GenerateCalculatorPDFV2/?azureId=<id>   (see api/calculator.service.js)
  Returns ServiceResponse<{ pdfData: <base64>, pdfUrl: <hosted link> }>.

  THE PAYLOAD IS A CONTRACT, NOT A SUGGESTION
  The server renders a fixed template against these exact property names, and every money
  value arrives as an already-formatted STRING, not a number. Rename a key or send a number
  and the field silently renders blank in the PDF rather than erroring. The shape below is
  copied from CalculatorV2.createPdfCalculatorObject(); do not "tidy" it.

  TWO DOCUMENTS, ONE ENDPOINT
  GSWebAPIv2's CalculatorService does NOT branch — it hands the whole payload to GSPDFFactory's
  CalculatorAmortizationV2 report, and THAT decides what to draw. Per the comment in CGS's own
  createPdfCalculatorObject(): the "Cash-Out Payoff Comparison" section renders only when
  PayoffAmount is a real, non-zero figure. A plain payment scenario sends $0.00 there and the
  PDF comes out as the ordinary payment sheet.

  WHICH IS WHY EMPTY STRING != "$0.00" HERE.
  CGS sends '' — not '$0.00' — for the cash-out comparison figures, the LTV when there is no
  price, the cash to borrower, and every "what they pay today" envelope field. The report
  treats a blank as "no value, omit the row" and a formatted zero as "the value is zero, print
  it". Sending $0.00 everywhere puts a row of zeroes on a sheet that should not have that
  section at all. Each `: ''` below is deliberate; match CGS exactly.

  HOA: the V2 report (GSPDFFactory CalculatorAmortizationV2) prints TotalMonthlyPayment
  verbatim, so the total goes out with HOA in it. MonthlyHOA is sent for the template's HOA line.

  AGENT IS OVERWRITTEN SERVER-SIDE.
  CalculatorService replaces FirstName/LastName/Phone/Email from Azure, adds the NMLS number
  from extensionAttribute11, the LO's photo and the DBA logo. The block still has to be present
  and non-null or the service throws — but filling it here is pointless, which is why CGS sends
  it empty and so do we. Note also: the service THROWS when the signed-in user has no NMLS # on
  their Azure profile, so that is a real, user-visible failure mode for this button.
*/

import calculatorService, { pick, unwrap } from '@/api/calculator.service.js'
import { AMORT_PDF_LABEL, AMORT_SHORT } from '@/helpers/paymentMath.js'

export function pdfMoney (n) {
  const v = Number(n)
  return (Number.isFinite(v) ? v : 0).toLocaleString('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2
  })
}

export function pdfPercent (n, dp = 3) {
  const v = Number(n)
  return (Number.isFinite(v) ? v : 0).toFixed(dp) + '%'
}

export function pdfTermLabel (months) {
  const m = Math.round(Number(months) || 0)
  if (!m) return '0 years'
  return (m % 12 === 0 ? String(m / 12) : (m / 12).toFixed(1)) + ' years'
}

function ls (key) {
  try { return localStorage.getItem(key) || '' } catch (e) { return '' }
}

export function azureId () {
  return ls('azure_id')
}

/*
  One captured scenario -> one LoanScenarioList row.

  `cashOut` is the optional block the cash-out wizard fills and the other calculators leave
  undefined. CGS emits '$0.00' / '' for those fields rather than omitting them, so a
  quick-mode PDF renders identically to how it always has.
*/
export function scenarioPdfRow ({ name, input, calc, cashOut }) {
  const co = cashOut || null
  const hasPayoff = !!(co && numOf(co.payoffAmount))
  const price = numOf(input.purchasePrice)
  // The Cash out field keeps its value when the refi type changes; only a cash-out refi sends it.
  const cashToBorrower = !calc.isBuy && input.refiType === 'Cash Out' ? numOf(input.cashOut) : 0
  const isFixed = input.loanAmort === 'Fixed'
  // GPM and GEM follow a set schedule, so their total interest is real. An ARM's total rests
  // on the index staying put, a caveat the template cannot print yet, and Other has no schedule.
  const hasInterestTotal = isFixed || input.loanAmort === 'Graduated Payment' || input.loanAmort === 'Growing Equity Mortgage'
  // The PDF's only "payment can change" signal until the template carries an Amortization row.
  let scenarioName = name || ''
  if (input.loanAmort && !isFixed) {
    const short = AMORT_SHORT[input.loanAmort] || input.loanAmort
    const named = scenarioName.includes(input.loanAmort) || scenarioName.includes(short)
    scenarioName += named ? ' (initial payment)' : ` · ${short} (initial payment)`
  }

  // `or0` mirrors CGS's `x ? money(x) : '$0.00'`: a falsy figure prints as a formatted zero.
  // `orBlank` mirrors `x ? money(x) : ''`: a falsy figure is omitted entirely. The difference
  // is what the report reads to decide whether a section exists at all -- see the header.
  const or0 = v => (numOf(v) ? pdfMoney(numOf(v)) : '$0.00')

  return {
    ScenarioName: scenarioName,
    SalePrice: or0(price),
    MortgageTerm: calc.termMonths ? pdfTermLabel(calc.termMonths) : '0 years',
    InterestRate: calc.annualPct ? pdfPercent(calc.annualPct) : '0%',
    TotalLoanAmount: or0(calc.loanAmount),
    MonthlyPrincipalPlusInterest: or0(calc.pandi),
    DownPayment: or0(calc.isBuy ? numOf(input.downPayment) : cashToBorrower),
    // An estimate (Other, or an ARM the engine could not model) is not printed as an APR.
    AnnualPercentageRate: calc.aprEstimate ? '' : calc.apr ? pdfPercent(calc.apr) : '0%',
    MonthlyTaxes: or0(calc.taxes),
    MonthlyHomeInsurance: or0(calc.insurance),
    MonthlyHOA: or0(calc.hoa),
    AmortizationType: AMORT_PDF_LABEL[input.loanAmort] || '',
    MonthlyMortgageInsurance: or0(calc.pmi),
    // HOA included: see the header note.
    TotalMonthlyPayment: calc.totalPayment ? pdfMoney(calc.totalPayment) : '$0.00',
    MonthlyTaxesAndInsurance: pdfMoney(numOf(calc.taxes) + numOf(calc.insurance)),
    TotalInterestPaid: hasInterestTotal ? or0(calc.totalInterestPaid) : '',

    // Blank, not zero, when there is no price to divide by.
    LoanToValue: price > 0 ? ((numOf(calc.loanAmount) / price) * 100).toFixed(1) + '%' : '',

    // ── the cash-out block ──
    // PayoffAmount is the switch: non-zero and the report draws the Cash-Out Payoff
    // Comparison; $0.00 and the sheet is an ordinary payment scenario. The four figures under
    // it go blank rather than zero so an ordinary sheet carries no empty comparison rows.
    PayoffAmount: hasPayoff ? pdfMoney(co.payoffAmount) : '$0.00',
    CurrentMonthlyPayment: hasPayoff ? pdfMoney(co.currentTotal) : '',
    NewPaymentWithPayoff: hasPayoff ? pdfMoney(co.withPayoffTotal) : '',
    NewPaymentWithoutPayoff: hasPayoff ? pdfMoney(co.withoutPayoffTotal) : '',
    EstimatedMonthlySavings: hasPayoff
      ? (co.monthlySavings >= 0
          ? `${pdfMoney(co.monthlySavings)} saved`
          : `${pdfMoney(Math.abs(co.monthlySavings))} more`)
      : '',
    PaidOffLiabilitiesSummary: payoffSummary(co && co.payoffLiabilities),
    CashToBorrower: cashToBorrower ? pdfMoney(cashToBorrower) : '',

    // Decided across the whole list in buildPdfPayload, not here.
    IsBestOption: false
  }
}

function numOf (v) {
  const n = parseFloat(String(v == null ? '' : v).replace(/[^0-9.-]/g, ''))
  return Number.isFinite(n) ? n : 0
}

// Three creditor names then "+N more". Deliberately short: a wrapping row breaks the column
// shading in the server's template.
export function payoffSummary (liabilities) {
  const names = (liabilities || []).map(l => String(l.companyName || '').trim()).filter(Boolean)
  if (!names.length) return ''
  if (names.length <= 3) return names.join(', ')
  return names.slice(0, 3).join(', ') + ` +${names.length - 3} more`
}

/*
  The whole request body. `scenarios` is a list of { name, input, calc, isBest, cashOut }.
  `context` carries the file-level fields the cash-out wizard knows and the others don't.
*/
export function buildPdfPayload ({ scenarios, context }) {
  const ctx = context || {}
  const list = scenarios || []
  const rows = list.map(scenarioPdfRow)

  // The report badges one card "Best monthly", and only this side can see every scenario at
  // once. CGS decides it among the CASH-OUT scenarios only -- on an ordinary payment sheet no
  // card is flagged, because the badge belongs to the comparison section.
  const cashOutIdx = list
    .map((s, i) => ({ s, i }))
    .filter(e => e.s.cashOut && numOf(e.s.cashOut.payoffAmount))
  if (cashOutIdx.length) {
    const winner = cashOutIdx.reduce((best, e) =>
      numOf(e.s.cashOut.withPayoffTotal) < numOf(best.s.cashOut.withPayoffTotal) ? e : best,
    cashOutIdx[0])
    rows[winner.i].IsBestOption = true
  }

  // Everything below describes what the borrower has TODAY, which is the same across every
  // scenario on the sheet, so it rides on the envelope rather than each card. Blank, not zero,
  // when there is no cash-out scenario -- otherwise an ordinary payment PDF grows a
  // "what you pay today" block full of zeroes.
  const hasCashOut = cashOutIdx.length > 0
  const orBlank = v => (numOf(v) ? pdfMoney(numOf(v)) : '')

  return {
    // Overwritten server-side from Azure; the block only has to exist. See the header note.
    Agent: { FirstName: '', LastName: '', Phone: '', Email: '', ImageDataUrl: '' },
    LoanScenarioList: rows,
    BorrowerName: ctx.borrowerName || '',
    LoanNumber: ctx.loanNumber || '',
    HomeValue: orBlank(ctx.homeValue),
    CurrentPrincipalAndInterest: hasCashOut ? pdfMoney(numOf(ctx.currentPandi)) : '',
    CurrentTaxesAndInsurance: hasCashOut ? pdfMoney(numOf(ctx.currentTaxesAndInsurance)) : '',
    CurrentOtherDebts: hasCashOut ? pdfMoney(numOf(ctx.currentOtherDebts)) : '',
    PayingTodayTotal: hasCashOut ? pdfMoney(numOf(ctx.payingTodayTotal)) : '',
    PayoffTotal: hasCashOut ? pdfMoney(numOf(ctx.payoffTotal)) : '',
    PayoffMonthlyTotal: hasCashOut ? pdfMoney(numOf(ctx.payoffMonthlyTotal)) : '',
    PayoffLiabilities: (ctx.payoffLiabilities || []).map(l => ({
      Creditor: l.companyName || '(unnamed creditor)',
      AccountType: l.accountType || '',
      Balance: pdfMoney(numOf(l.unpaidBalance)),
      // Blank, not $0.00 -- a creditor with no payment on file is not a creditor charging zero.
      MonthlyPayment: numOf(l.monthlyAmount) ? pdfMoney(numOf(l.monthlyAmount)) : ''
    }))
  }
}

// base64 -> object URL, in 512-byte slices. atob on a multi-megabyte string in one go is what
// makes a browser tab hang, which is why CGS chunks it and so does this.
export function base64ToBlobUrl (base64) {
  const clean = String(base64 || '').replace(/^data:application\/pdf;base64,/, '')
  if (!clean) return ''
  const bytes = atob(clean)
  const chunks = []
  for (let offset = 0; offset < bytes.length; offset += 512) {
    const slice = bytes.slice(offset, offset + 512)
    const nums = new Array(slice.length)
    for (let i = 0; i < slice.length; i++) nums[i] = slice.charCodeAt(i)
    chunks.push(new Uint8Array(nums))
  }
  return URL.createObjectURL(new Blob(chunks, { type: 'application/pdf' }))
}

/*
  Generate and return { blobUrl, hostedUrl }.

  blobUrl is local to this tab — good for the viewer, the download and printing, useless in an
  email. hostedUrl is the server-side link, which is the one to paste to a borrower. The caller
  is responsible for URL.revokeObjectURL(blobUrl) when the viewer closes.
*/
export async function generatePdf ({ payload, token }) {
  const response = await calculatorService.GenerateCalculatorPDFV2(azureId(), payload, token)
  const data = unwrap(response) || {}
  const base64 = pick(data, 'pdfData')
  const hostedUrl = pick(data, 'pdfUrl') || ''
  if (!base64 && !hostedUrl) throw new Error('The PDF service did not return a document.')
  return { blobUrl: base64 ? base64ToBlobUrl(base64) : '', hostedUrl }
}
